import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { BlogCategory } from '@nucleus-commerce/api-client/admin';

/** Column model for the blog categories table: search over title and description, three sorts. */
export function useBlogCategoryColumns() {
  const { t } = useTranslation('blog');
  return useMemo<Array<DataTableColumnDef<BlogCategory>>>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: t('categories.columns.category'),
        meta: { sortField: 'title', lockVisibility: true },
        cell: ({ row }) => (
          <div className="min-w-0">
            <span className="block truncate font-medium">{row.original.title}</span>
            <code className="text-muted-foreground block truncate text-xs">
              /blog/category/{row.original.handle}
            </code>
          </div>
        ),
      },
      {
        id: 'description',
        accessorFn: (row) => row.description ?? '',
        header: t('categories.columns.description'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.description ? (
            <span className="text-muted-foreground line-clamp-2 text-sm whitespace-normal">
              {row.original.description}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('none')}</span>
          ),
      },
      {
        id: 'position',
        accessorKey: 'position',
        header: t('categories.columns.position'),
        meta: { sortField: 'position', searchable: false },
        cell: ({ row }) => <span className="tabular-nums">{row.original.position}</span>,
      },
      {
        id: 'postCount',
        accessorKey: 'postCount',
        header: t('categories.columns.posts'),
        meta: { sortField: 'postCount', searchable: false },
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('categories.postCount', { count: row.original.postCount })}
          </span>
        ),
      },
    ],
    [t],
  );
}
